import { InfoOutlined } from "@mui/icons-material";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { ButtonBase, Link, Stack, useTheme } from "@mui/material";
import { sumBy } from "lodash";
import React, { useMemo } from "react";

import { Alert, AlertTitle } from "@/Components/v2/atoms/Alert";
import { Box } from "@/Components/v2/atoms/Box";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { DollarTextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { FormCard } from "@/Components/v2/molecule/FormContainer";
import { useConfig } from "@/hooks/useConfig";
import { isDefined } from "@/Utils/array-utils";

import { confirmResetScenarioConstraints } from "../../components/ResetScenarioConstraintsConfirmation";
import { useScenariosV3Routes } from "../../hooks/useScenariosV3Routes";
import { useScenarioForms } from "./ScenarioFormProvider";
import { confirmSpendGeographicGranularity } from "./SpendGeographicGranularityConfirmation";
import { ScenarioTypeEnum } from "./types";

interface MediaSpendConstraintsV4Props {
  disabled: boolean;
}

const MediaSpendConstraints: React.FC<MediaSpendConstraintsV4Props> = ({ disabled }) => {
  const theme = useTheme();
  const { uuid, navigateToChannelSpend } = useScenariosV3Routes();
  const { handleResetConstraints } = useScenarioForms();
  const {
    scenario,
    mediaConstraintsForm,
    spendInputForm,
    budgetAllocationForm,
    geoStatus,
    handleSpendGeographicGranularityChange,
  } = useScenarioForms();

  const handleNavigateToChannelSpend = async () => {
    if (disabled) return;

    const watchedMediaConstraints = mediaConstraintsForm.getValues("mediaConstraints");
    const watchedSpendInputs = spendInputForm.getValues("spendInputs");

    if (
      geoStatus.type === "all_geographies" &&
      watchedMediaConstraints?.length === 0 &&
      watchedSpendInputs?.length === 0
    ) {
      const confirmedGeographyGranularityChoice = await confirmSpendGeographicGranularity({
        theme,
      });
      if (!confirmedGeographyGranularityChoice) return;

      await handleSpendGeographicGranularityChange(confirmedGeographyGranularityChoice);
    }

    navigateToChannelSpend(uuid);
  };

  const budgetNotSet = !isDefined(budgetAllocationForm.watch("totalBudget"));

  const { config } = useConfig();

  const currencySymbol = config.currency.getSymbol();

  const hasErrors =
    Object.keys(mediaConstraintsForm.formState.errors).length > 0 ||
    Object.keys(spendInputForm.formState.errors).length > 0;

  const watchedMediaConstraints = mediaConstraintsForm.watch("mediaConstraints");
  const watchedSpendInputs = spendInputForm.watch("spendInputs");

  const handleResetConstraintsClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = await confirmResetScenarioConstraints({ theme });
    if (confirmed) {
      await handleResetConstraints();
    }
  };

  const spendInputsSummary = useMemo(() => {
    if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
      if (watchedMediaConstraints?.length === 0) {
        return;
      }

      const totalMinConstraints = sumBy(watchedMediaConstraints ?? [], "minValue") ?? 0;

      const totalMaxConstraints = sumBy(watchedMediaConstraints ?? [], "maxValue") ?? 0;

      return {
        minSpend: totalMinConstraints,
        maxSpend: totalMaxConstraints,
      };
    }

    if (watchedSpendInputs?.length === 0) {
      return;
    }

    const totalSpend = sumBy(watchedSpendInputs ?? [], "spend") ?? 0;

    return {
      maxSpend: totalSpend,
    };
  }, [watchedSpendInputs, watchedMediaConstraints, scenario?.scenarioType]);

  return (
    <FormCard title="Inputs" subtitle="Set spending rules for this scenario.">
      <ButtonBase
        disabled={budgetNotSet}
        onClick={handleNavigateToChannelSpend}
        sx={{
          width: "100%",
          border: 1,
          borderRadius: 1,
          textAlign: "left",
          opacity: budgetNotSet ? 0.6 : 1,
          "&:hover": {
            bgcolor: "channelInputs.backgroundHover",
          },
          borderColor: hasErrors ? "error.main" : "channelInputs.border",
          display: "flex",
          alignItems: "stretch",
          p: 3,
          gap: 2,
        }}
      >
        <Stack justifyContent="start">
          <CampaignOutlinedIcon
            sx={{
              fontSize: "2rem",
              color: budgetNotSet ? "text.disabled" : "text.primary",
            }}
          />
        </Stack>
        <Stack direction="column" sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: budgetNotSet ? "text.disabled" : "text.primary",
                    fontWeight: 500,
                  }}
                >
                  Channel Inputs
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: budgetNotSet ? "text.disabled" : "text.primary",
                  }}
                >
                  Set spending rules by media channel
                </Typography>
              </Box>
            </Stack>
            <ArrowForwardIosOutlinedIcon
              sx={{
                color: budgetNotSet ? "text.disabled" : "text.primary",
              }}
            />
          </Stack>

          {spendInputsSummary && (
            <Stack
              direction="row"
              spacing={2}
              sx={{ pt: 2 }}
              justifyContent="space-between"
              alignItems="center"
              flex={1}
            >
              <Stack direction="row" spacing={2} flex={1}>
                <Stack spacing={2} direction="row">
                  {isDefined(spendInputsSummary.minSpend) && (
                    <FormControl margin="normal" sx={{ maxWidth: "600px" }}>
                      <Typography fontWeight={600} mb={1} variant="body2">
                        Total Min Constraints
                      </Typography>
                      <DollarTextField
                        value={spendInputsSummary.minSpend}
                        disabled={true}
                        size="small"
                        currencySymbol={currencySymbol}
                        onChange={() => {}}
                      />
                    </FormControl>
                  )}
                  <FormControl margin="normal" sx={{ maxWidth: "600px" }}>
                    <Typography fontWeight={600} mb={1} variant="body2">
                      Total Max Constraints
                    </Typography>
                    <DollarTextField
                      value={spendInputsSummary.maxSpend}
                      disabled={true}
                      size="small"
                      currencySymbol={currencySymbol}
                      onChange={() => {}}
                    />
                  </FormControl>
                </Stack>
              </Stack>

              <Stack>
                <Link
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                  onClick={handleResetConstraintsClick}
                >
                  <Typography
                    variant="body2"
                    sx={{ textDecoration: "underline", color: "text.primary" }}
                  >
                    Reset all channel inputs
                  </Typography>
                </Link>
              </Stack>
            </Stack>
          )}

          {hasErrors && (
            <Alert
              severity="error"
              icon={<InfoOutlined />}
              sx={{ mt: 1, backgroundColor: "error.light", color: "common.black" }}
            >
              <AlertTitle sx={{ fontWeight: 600, mb: 1, color: "common.black" }}>
                Invalid constraints
              </AlertTitle>

              <Typography variant="body2" sx={{ mb: 1, color: "common.black" }}>
                One or more scenario constraints are invalid, probably because you have adjusted the
                start or end date. Please update or remove them before submitting
              </Typography>
            </Alert>
          )}
        </Stack>

        {/* </Stack> */}
      </ButtonBase>
    </FormCard>
  );
};

export default MediaSpendConstraints;
